/**
 * Horizontal scroll with a cue: a fade at an edge while columns are hidden
 * past it, so a cut-off cell reads as "more to the right", not as a short
 * value. DataTable draws the same fades over its own scroller.
 */
import { useRef, type ReactNode } from "react";
import { useScrollEdges, type ScrollEdges } from "@/lib/useScrollEdges";

/** The fades for a scroller; place inside a `relative` box that wraps it. */
export function EdgeFades({ edges, className = "inset-y-0" }: { edges: ScrollEdges; className?: string }) {
  const fade = `pointer-events-none absolute z-10 w-6 from-bg-elev via-bg-elev/70 to-transparent ${className}`;
  return (
    <>
      {edges.left && <div aria-hidden className={`${fade} left-0 bg-linear-to-r`} />}
      {edges.right && (
        <div aria-hidden className={`${fade} bg-linear-to-l`} style={{ right: edges.gutter }} />
      )}
    </>
  );
}

export function ScrollX({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(ref);
  return (
    <div className={`relative min-w-0 overflow-hidden ${className}`}>
      <div ref={ref} className="overflow-x-auto">
        {children}
      </div>
      <EdgeFades edges={edges} />
    </div>
  );
}
